import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  ChartBarIcon,
  UsersIcon,
  CreditCardIcon,
  BookOpenIcon,
  CogIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const AnalyticsPage = () => {
  const [userChartData, setUserChartData] = useState(null);
  const [paymentChartData, setPaymentChartData] = useState(null);
  const [courseChartData, setCourseChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('daily');
  const [year, setYear] = useState(new Date().getFullYear());
  const [chartType, setChartType] = useState('users');

  useEffect(() => {
    fetchChartData();
  }, [period, year]);

  const fetchChartData = async () => {
    try {
      setLoading(true);

      const [usersRes, paymentsRes, coursesRes] = await Promise.all([
        axios.get(`/api/analytics/users-chart?period=${period}&year=${year}`),
        axios.get(`/api/analytics/payments-chart?period=${period}&year=${year}`),
        axios.get(`/api/analytics/courses-chart?period=${period}&year=${year}`)
      ]);

      setUserChartData(usersRes.data);
      setPaymentChartData(paymentsRes.data);
      setCourseChartData(coursesRes.data);
    } catch (error) {
      console.error('Error fetching chart data:', error);
    } finally {
      setLoading(false);
    }
  };

  const userChartConfig = {
    labels: userChartData?.data.map(item => {
      if (period === 'monthly') {
        const [year, month] = item.date.split('-');
        return `${month}/${year}`;
      } else {
        const [, month, day] = item.date.split('-');
        return `${month}/${day}`;
      }
    }) || [],
    datasets: [
      {
        label: 'Total Registrations',
        data: userChartData?.data.map(item => item.total) || [],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        fill: true,
      },
      {
        label: 'Students',
        data: userChartData?.data.map(item => item.students) || [],
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.5)',
        fill: true,
      },
      {
        label: 'Instructors',
        data: userChartData?.data.map(item => item.instructors) || [],
        borderColor: 'rgb(245, 158, 11)',
        backgroundColor: 'rgba(245, 158, 11, 0.5)',
        fill: true,
      },
    ],
  };

  const paymentChartConfig = {
    labels: paymentChartData?.data.map(item => {
      if (period === 'monthly') {
        const [year, month] = item.date.split('-');
        return `${month}/${year}`;
      } else {
        const [, month, day] = item.date.split('-');
        return `${month}/${day}`;
      }
    }) || [],
    datasets: [
      {
        label: 'Successful Payments',
        data: paymentChartData?.data.map(item => item.successfulPayments) || [],
        backgroundColor: 'rgba(16, 185, 129, 0.8)',
        borderColor: 'rgb(16, 185, 129)',
        borderWidth: 1,
      },
      {
        label: 'Failed Payments',
        data: paymentChartData?.data.map(item => item.failedPayments) || [],
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
        borderColor: 'rgb(239, 68, 68)',
        borderWidth: 1,
      },
    ],
  };

  const courseChartConfig = {
    labels: courseChartData?.data.map(item => {
      if (period === 'monthly') {
        const [year, month] = item.date.split('-');
        return `${month}/${year}`;
      } else {
        const [, month, day] = item.date.split('-');
        return `${month}/${day}`;
      }
    }) || [],
    datasets: [
      {
        label: 'New Courses',
        data: courseChartData?.data.map(item => item.newCourses) || [],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        fill: true,
      },
      {
        label: 'Approved Courses',
        data: courseChartData?.data.map(item => item.approvedCourses) || [],
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.5)',
        fill: true,
      },
    ],
  };

  const paymentSuccessRateData = {
    labels: ['Successful', 'Failed'],
    datasets: [{
      data: [
        paymentChartData?.overallSuccessRate || 0,
        100 - (paymentChartData?.overallSuccessRate || 0)
      ],
      backgroundColor: [
        'rgba(16, 185, 129, 0.8)',
        'rgba(239, 68, 68, 0.8)',
      ],
      borderColor: [
        'rgb(16, 185, 129)',
        'rgb(239, 68, 68)',
      ],
      borderWidth: 1,
    }],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: `${chartType.charAt(0).toUpperCase() + chartType.slice(1)} Analytics - ${period === 'monthly' ? 'Monthly' : 'Daily'} View (${year})`,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: `Payment Success Rate (${paymentChartData?.overallSuccessRate || 0}%)`,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.parsed;
            return `${context.label}: ${value.toFixed(1)}%`;
          },
        },
      },
    },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white">
        <div className="flex items-center space-x-3">
          <ChartBarIcon className="h-8 w-8" />
          <div>
            <h1 className="text-2xl font-bold">Analytics Dashboard</h1>
            <p className="text-blue-100">
              Detailed insights into platform performance and user engagement
            </p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex space-x-2">
            <button
              onClick={() => setChartType('users')}
              className={`btn ${chartType === 'users' ? 'btn-primary' : 'btn-secondary'}`}
            >
              <UsersIcon className="h-4 w-4 mr-2" />
              User Registrations
            </button>
            <button
              onClick={() => setChartType('payments')}
              className={`btn ${chartType === 'payments' ? 'btn-primary' : 'btn-secondary'}`}
            >
              <CreditCardIcon className="h-4 w-4 mr-2" />
              Payment Analytics
            </button>
            <button
              onClick={() => setChartType('courses')}
              className={`btn ${chartType === 'courses' ? 'btn-primary' : 'btn-secondary'}`}
            >
              <BookOpenIcon className="h-4 w-4 mr-2" />
              Course Creation
            </button>
          </div>

          <div className="flex space-x-2">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="input"
            >
              <option value="daily">Daily View</option>
              <option value="monthly">Monthly View</option>
            </select>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="input"
            >
              <option value={new Date().getFullYear()}>{new Date().getFullYear()}</option>
              <option value={new Date().getFullYear() - 1}>{new Date().getFullYear() - 1}</option>
              <option value={new Date().getFullYear() - 2}>{new Date().getFullYear() - 2}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Primary Chart */}
            <div className="h-80">
              {chartType === 'users' && userChartData && (
                <Line data={userChartConfig} options={chartOptions} />
              )}
              {chartType === 'payments' && paymentChartData && (
                <Bar data={paymentChartConfig} options={chartOptions} />
              )}
              {chartType === 'courses' && courseChartData && (
                <Line data={courseChartConfig} options={chartOptions} />
              )}
            </div>

            {/* Payment Success Rate - Only show for payments */}
            {chartType === 'payments' && paymentChartData && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="h-64">
                  <Doughnut data={paymentSuccessRateData} options={doughnutOptions} />
                </div>
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Payment Summary</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                      <span className="text-green-700 font-medium">Overall Success Rate</span>
                      <span className="text-2xl font-bold text-green-600">
                        {paymentChartData.overallSuccessRate || 0}%
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                      <span className="text-blue-700 font-medium">Total Transactions</span>
                      <span className="text-2xl font-bold text-blue-600">
                        {paymentChartData.data.reduce((sum, item) => sum + item.totalInitiated, 0)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                      <span className="text-purple-700 font-medium">Total Revenue</span>
                      <span className="text-2xl font-bold text-purple-600">
                        ₹{paymentChartData.data.reduce((sum, item) => sum + item.totalRevenue, 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Summary Stats for other charts */}
            {chartType === 'users' && userChartData && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="text-blue-700 font-semibold">Total Users</h4>
                  <p className="text-3xl font-bold text-blue-600">
                    {userChartData.data.reduce((sum, item) => sum + item.total, 0)}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="text-green-700 font-semibold">Students</h4>
                  <p className="text-3xl font-bold text-green-600">
                    {userChartData.data.reduce((sum, item) => sum + item.students, 0)}
                  </p>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h4 className="text-yellow-700 font-semibold">Instructors</h4>
                  <p className="text-3xl font-bold text-yellow-600">
                    {userChartData.data.reduce((sum, item) => sum + item.instructors, 0)}
                  </p>
                </div>
              </div>
            )}

            {chartType === 'courses' && courseChartData && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="text-blue-700 font-semibold">New Courses</h4>
                  <p className="text-3xl font-bold text-blue-600">
                    {courseChartData.data.reduce((sum, item) => sum + item.newCourses, 0)}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="text-green-700 font-semibold">Approved Courses</h4>
                  <p className="text-3xl font-bold text-green-600">
                    {courseChartData.data.reduce((sum, item) => sum + item.approvedCourses, 0)}
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h4 className="text-purple-700 font-semibold">Total Enrollments</h4>
                  <p className="text-3xl font-bold text-purple-600">
                    {courseChartData.data.reduce((sum, item) => sum + item.totalEnrollments, 0)}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsPage;
